import { ArrowRight, FileQuestion, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, Link, useLocation, useRouteError } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader, useDocumentTitle } from '../components/PageHeader';
import { ErrorState } from '../components/States';

export function NotFoundPage() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  useDocumentTitle(t('errors.notFound'));
  const [before, after] = t('errors.nothingAt', { path: '\u0000' }).split('\u0000');
  return (
    <>
      <PageHeader title={t('errors.notFound')} subtitle={t('errors.notFoundBody')} />
      <Card className="max-w-2xl shadow-xs">
        <CardContent className="flex flex-col items-start gap-4 sm:flex-row">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <FileQuestion className="size-5 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="flex flex-col items-start gap-4 text-sm">
            <p>
              {before}
              <code className="rounded bg-muted px-1.5 py-0.5 break-all">{pathname}</code>
              {after}
            </p>
            <Button asChild>
              <Link to="/">
                {t('errors.goHome')} <ArrowRight />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/** Route-level error boundary: a crash in one page must not take down the shell. */
export function RouteErrorPage() {
  const { t } = useTranslation();
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  return (
    <div className="mx-auto flex max-w-2xl flex-col items-start gap-3 py-8">
      <ErrorState title={t('errors.crashed')} error={error} />
      <p className="text-sm text-muted-foreground">{t('errors.crashedHint')}</p>
      <Button type="button" variant="outline" onClick={() => window.location.reload()}>
        <RefreshCw /> {t('errors.reload')}
      </Button>
    </div>
  );
}
